import React, { useState, useRef, useEffect } from 'react';
import { fabric } from 'fabric';
import { useNavigate } from 'react-router-dom';

function QuickOrder() {
  // Add step state
  const [currentStep, setCurrentStep] = useState(1);  // 1 for design, 2 for sizing

  // Existing state
  const [selectedColor, setSelectedColor] = useState('black');
  const [quantities, setQuantities] = useState({
    'S': 0,
    'M': 0,
    'L': 0,
    'XL': 0,
    '2XL': 0
  });
  const [uploadError, setUploadError] = useState('');
  const [textInput, setTextInput] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const [selectedText, setSelectedText] = useState(null);
  
  // Add text property states
  const [fontSize, setFontSize] = useState(30);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [textColor, setTextColor] = useState('#000000');
  
  // Existing refs
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);

  // Constants
  const SHIRT_COLORS = {
    black: {
      name: 'Black',
      hex: '#000000',
      image: '/images/mockups/blackblank.png'
    },
    white: {
      name: 'White',
      hex: '#FFFFFF',
      image: '/images/mockups/whiteblank.png'
    },
    cardinal: {
      name: 'Cardinal',
      hex: '#C41E3A',
      image: '/images/mockups/cardinalblank.png'
    }
  };

  // Available fonts
  const fontOptions = [
    'Arial',
    'Times New Roman',
    'Helvetica',
    'Verdana',
    'Georgia',
    'Courier New',
    'Impact'
  ];

  // Update state to track multiple text objects
  const [textLayers, setTextLayers] = useState([]);

  // Initialize canvas with selection events
  useEffect(() => {
    const initCanvas = () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
      }

      if (canvasRef.current) {
        try {
          const canvas = new fabric.Canvas(canvasRef.current, {
            width: 600,
            height: 880,
            backgroundColor: '#f9fafb',
            selection: true  // Enable selection
          });

          // Add selection event listeners
          canvas.on('selection:created', (e) => {
            if (e.selected[0] instanceof fabric.Text) {
              setSelectedText(e.selected[0]);
            }
          });

          canvas.on('selection:cleared', () => {
            setSelectedText(null);
          });

          fabricCanvasRef.current = canvas;
          loadShirtImage(canvas);
        } catch (error) {
          console.error('Error initializing canvas:', error);
        }
      }
    };

    initCanvas();
  }, []);

  // Load shirt image
  const loadShirtImage = (canvasInstance) => {
    const canvas = canvasInstance || fabricCanvasRef.current;
    if (!canvas) return;

    const img = new window.Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        // Create fabric image
        const fabricImage = new fabric.Image(img);

        // Calculate dimensions that will fit in the canvas while maintaining aspect ratio
        const containerWidth = 600;
        const containerHeight = 880;
        
        // Calculate scale to fit the container while maintaining aspect ratio
        const scaleX = (containerWidth * 1.1) / img.width;
        const scaleY = (containerHeight * 1.1) / img.height;
        const scale = Math.min(scaleX, scaleY);

        // Set image properties
        fabricImage.set({
          selectable: false,
          evented: false,
          scaleX: scale,
          scaleY: scale,
          originX: 'center',
          originY: 'center',
          left: containerWidth / 2,
          top: (containerHeight / 2) - 40
        });

        // Remove existing shirt if present
        const objects = canvas.getObjects();
        if (objects.length > 0) {
          canvas.remove(objects[0]);
        }
        
        // Add to canvas
        canvas.add(fabricImage);
        canvas.moveTo(fabricImage, 0);  // Move to back of stack
        canvas.renderAll();

      } catch (error) {
        console.error('Error adding shirt image to canvas:', error);
      }
    };

    img.src = SHIRT_COLORS[selectedColor].image;
  };

  // Update shirt when color changes
  useEffect(() => {
    if (fabricCanvasRef.current) {
      loadShirtImage();
    }
  }, [selectedColor]);

  // Function to validate image
  const validateImage = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const valid = img.width >= 1000 && 
                     img.height >= 1000 && 
                     file.size >= 1024 * 1024; // 1MB in bytes
        
        if (!valid) {
          reject('Image must be at least 1000x1000 pixels and 1MB in size');
        } else {
          resolve(img);
        }
      };
      img.onerror = () => reject('Failed to load image');
      img.src = URL.createObjectURL(file);
    });
  };

  // Function to add image to canvas
  const addImageToCanvas = (img) => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    
    // Create a new image element
    const imgElement = new Image();
    imgElement.crossOrigin = 'anonymous';
    imgElement.src = img.src;
    
    imgElement.onload = () => {
      const fabricImg = new fabric.Image(imgElement);
      
      // Scale image to reasonable initial size while maintaining aspect ratio
      const scale = Math.min(
        (canvas.width * 0.3) / imgElement.width,
        (canvas.height * 0.3) / imgElement.height
      );
      
      // Make image interactive
      fabricImg.set({
        left: canvas.width / 2,        // Center horizontally
        top: canvas.height / 2 - 40,   // Center vertically (adjusted like shirt)
        originX: 'center',
        originY: 'center',
        scaleX: scale,
        scaleY: scale,
        cornerColor: '#3b82f6',
        cornerStrokeColor: '#2563eb',
        cornerStyle: 'circle',
        transparentCorners: false,
        cornerSize: 12,
        padding: 5,
        borderColor: '#3b82f6',
        borderScaleFactor: 2,
        centeredRotation: true,
        centeredScaling: true
      });

      // Remove all objects except the shirt (first object)
      const objects = canvas.getObjects();
      const shirtImage = objects[0];
      canvas.clear();
      
      // Re-add shirt and new image in correct order
      canvas.add(shirtImage);
      canvas.add(fabricImg);
      
      canvas.setActiveObject(fabricImg);
      canvas.renderAll();
    };
  };

  // Function to handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Validate image
      const img = await validateImage(file);
      
      // Add to canvas
      addImageToCanvas(img);
      
      // Clear error and file input
      setUploadError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      setUploadError(error.toString());
    }
  };

  // Function to add text to canvas
  const addTextToCanvas = (text) => {
    if (!fabricCanvasRef.current || !text.trim()) return;

    const canvas = fabricCanvasRef.current;
    
    const fabricText = new fabric.Text(text, {
      left: canvas.width / 2,
      top: canvas.height / 2 - 40,
      originX: 'center',
      originY: 'center',
      fontSize: fontSize,
      fontFamily: fontFamily,
      fill: textColor,
      cornerColor: '#3b82f6',
      cornerStrokeColor: '#2563eb',
      cornerStyle: 'circle',
      transparentCorners: false,
      padding: 5,
      borderColor: '#3b82f6',
      borderScaleFactor: 2,
      centeredRotation: true,
      centeredScaling: true
    });

    canvas.add(fabricText);
    canvas.setActiveObject(fabricText);
    canvas.renderAll();

    // Reset text input
    setTextInput('');
    setShowTextInput(false);
  };

  // Function to export canvas as JPEG
  const exportCanvasAsJpeg = () => {
    if (!fabricCanvasRef.current) return;

    try {
      // Get canvas data URL
      const dataURL = fabricCanvasRef.current.toDataURL({
        format: 'jpeg',
        quality: 1.0
      });

      // Create a temporary link element
      const link = document.createElement('a');
      link.href = dataURL;
      link.download = 'shirt-design.jpg';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting canvas:', error);
    }
  };

  // Function to update selected text
  const updateSelectedText = (property, value) => {
    if (!selectedText || !fabricCanvasRef.current) return;

    if (property === 'text') {
      setTextInput(value);  // Update the input state
      selectedText.set('text', value);  // Update the canvas text
    } else if (property === 'fontFamily') {
      selectedText.set({
        fontFamily: value,
        dirty: true  // Force a redraw
      });
      setFontFamily(value);  // Update the state
    } else {
      selectedText.set(property, value);
    }
    
    fabricCanvasRef.current.renderAll();
  };

  // Function to add new text to canvas
  const addNewText = () => {
    if (!fabricCanvasRef.current) return;
    
    const text = new fabric.Text('Enter text here...', {
      left: 250,
      top: 250,
      fontFamily: 'Arial',
      fontSize: 30,
      fill: '#000000',
      selectable: true,
      editable: true,
      id: Date.now()
    });
    
    fabricCanvasRef.current.add(text);
    fabricCanvasRef.current.setActiveObject(text);
    setSelectedText(text);
    
    const newLayer = { 
      id: text.id,
      text: text,
      inputValue: 'Enter text here...',
      isNew: true  // Add flag for new layers
    };
    
    setTextLayers(prev => [...prev, newLayer]);
  };

  // Function to close text editor
  const closeTextEditor = () => {
    setSelectedText(null);
    setShowTextInput(false);
  };

  // Add delete function
  const deleteSelectedText = () => {
    if (!selectedText || !fabricCanvasRef.current) return;
    
    fabricCanvasRef.current.remove(selectedText);
    fabricCanvasRef.current.renderAll();
    setSelectedText(null);
    setShowTextInput(false);  // Close the editor
  };

  // Update the keyboard event listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if we're currently focused in an input or contenteditable element
      const isInputActive = document.activeElement.tagName === 'INPUT' || 
                           document.activeElement.tagName === 'TEXTAREA' ||
                           document.activeElement.isContentEditable;
      
      // Only handle delete if we're not in an input and have selected text
      if (!isInputActive && selectedText && (e.key === 'Delete' || e.key === 'Backspace')) {
        fabricCanvasRef.current?.remove(selectedText);
        fabricCanvasRef.current?.renderAll();
        setTextLayers(prev => prev.filter(layer => layer.text !== selectedText));
        setSelectedText(null);
        setShowTextInput(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedText]);

  // Add pricing tiers
  const getPricePerShirt = (totalQuantity) => {
    if (totalQuantity >= 25) return 13;
    if (totalQuantity >= 9) return 16;
    if (totalQuantity >= 5) return 19;
    return 21;
  };

  // Calculate total price
  const calculateTotal = () => {
    const totalQuantity = Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
    const pricePerShirt = getPricePerShirt(totalQuantity);
    return totalQuantity * pricePerShirt;
  };

  // Calculate delivery date (6 business days)
  const getDeliveryDate = () => {
    let date = new Date();
    let businessDays = 6;
    while (businessDays > 0) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        businessDays--;
      }
    }
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const navigate = useNavigate();

  const renderDesignStep = () => (
    <>
      {/* Color Selection */}
      <div style={{ marginBottom: '2rem' }}>
        <label style={{ 
          display: 'block',
          fontSize: '0.875rem',
          fontWeight: '500',
          marginBottom: '0.75rem',
          color: '#4b5563'
        }}>
          Select Color
        </label>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {Object.entries(SHIRT_COLORS).map(([color, details]) => (
            <button
              key={color}
              onClick={() => setSelectedColor(color)}
              style={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '9999px',
                backgroundColor: details.hex,
                border: selectedColor === color ? '2px solid #98F9B3' : '2px solid #e5e7eb',
                transform: selectedColor === color ? 'scale(1.1)' : 'scale(1)',
                transition: 'all 0.2s'
              }}
              title={details.name}
            />
          ))}
        </div>
      </div>

      {/* Upload Design Button */}
      <div style={{ marginBottom: '1rem' }}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept="image/*"
          style={{ display: 'none' }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          style={{
            width: '100%',
            backgroundColor: '#98F9B3',
            color: '#000000',
            padding: '0.75rem',
            borderRadius: '0',
            fontWeight: '500',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'Montserrat, sans-serif',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
        >
          Upload Design
        </button>
      </div>

      {/* Text Controls */}
      {showTextInput ? (
        <div style={{ 
          marginBottom: '1rem',
          padding: '1rem',
          backgroundColor: '#3D3D3D',
          border: '1px solid #98F9B3',
          borderRadius: '0'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h3 style={{ 
              fontSize: '0.875rem',
              color: '#FFFFFF',
              fontFamily: 'Montserrat, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Edit Text
            </h3>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={deleteSelectedText}
                style={{
                  backgroundColor: '#FF4444',  // Red for delete
                  border: 'none',
                  color: '#000000',
                  padding: '0.5rem 0.75rem',
                  cursor: 'pointer',
                  fontFamily: 'Montserrat, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '0.75rem',
                  borderRadius: '0'
                }}
              >
                Delete
              </button>
              <button
                onClick={closeTextEditor}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#98F9B3',
                  cursor: 'pointer',
                  fontFamily: 'Montserrat, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '0.75rem'
                }}
              >
                Close
              </button>
            </div>
          </div>

          {/* Text Content */}
          <input
            type="text"
            value={textInput}
            onChange={(e) => updateSelectedText('text', e.target.value)}
            autoFocus
            onFocus={(e) => e.target.select()}
            style={{
              width: '100%',
              padding: '0.5rem',
              marginBottom: '1rem',
              backgroundColor: '#2D2D2D',
              border: '1px solid #98F9B3',
              borderRadius: '0',
              color: '#FFFFFF',
              fontFamily: 'Montserrat, sans-serif'
            }}
          />

          {/* Font Family */}
          <select
            value={selectedText?.fontFamily || fontFamily}
            onChange={(e) => updateSelectedText('fontFamily', e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              marginBottom: '1rem',
              backgroundColor: '#2D2D2D',
              border: '1px solid #98F9B3',
              borderRadius: '0',
              color: '#FFFFFF',
              fontFamily: 'Montserrat, sans-serif',
              cursor: 'pointer'
            }}
          >
            {fontOptions.map(font => (
              <option key={font} value={font} style={{ fontFamily: font }}>
                {font}
              </option>
            ))}
          </select>

          {/* Font Size */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block',
              marginBottom: '0.5rem',
              color: '#FFFFFF',
              fontSize: '0.875rem',
              fontFamily: 'Montserrat, sans-serif',
              textTransform: 'uppercase'
            }}>
              Size: {selectedText?.fontSize}px
            </label>
            <input
              type="range"
              min="12"
              max="72"
              value={selectedText?.fontSize}
              onChange={(e) => updateSelectedText('fontSize', parseInt(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>

          {/* Text Color */}
          <div>
            <label style={{ 
              display: 'block',
              marginBottom: '0.5rem',
              color: '#FFFFFF',
              fontSize: '0.875rem',
              fontFamily: 'Montserrat, sans-serif',
              textTransform: 'uppercase'
            }}>
              Color
            </label>
            <input
              type="color"
              value={selectedText?.fill}
              onChange={(e) => updateSelectedText('fill', e.target.value)}
              style={{
                width: '100%',
                height: '40px',
                padding: '0',
                border: '1px solid #98F9B3',
                borderRadius: '0'
              }}
            />
          </div>
        </div>
      ) : (
        <>
          {textLayers.map((layer) => (
            <div 
              key={layer.id}
              style={{ 
                marginBottom: '1rem',
                padding: '1rem',
                backgroundColor: '#3D3D3D',
                border: `1px solid ${selectedText?.id === layer.id ? '#98F9B3' : '#4D4D4D'}`,
                borderRadius: '0'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h3 style={{ 
                  fontSize: '0.875rem',
                  color: '#FFFFFF',
                  fontFamily: 'Montserrat, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Text Layer {textLayers.indexOf(layer) + 1}
                </h3>
              </div>

              <input
                type="text"
                value={layer.inputValue || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  layer.text.set('text', newValue);
                  fabricCanvasRef.current?.renderAll();
                  setTextLayers(prev => prev.map(t => 
                    t.id === layer.id 
                      ? { ...t, inputValue: newValue, isNew: false }
                      : t
                  ));
                }}
                autoFocus={layer.isNew}  // Auto focus if it's a new layer
                onFocus={(e) => {
                  if (layer.isNew || layer.inputValue === 'Enter text here...') {
                    e.target.select();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  marginBottom: '1rem',
                  backgroundColor: '#2D2D2D',
                  border: '1px solid #98F9B3',
                  borderRadius: '0',
                  color: '#FFFFFF',
                  fontFamily: 'Montserrat, sans-serif'
                }}
              />

              <select
                value={layer.text.fontFamily || 'Arial'}
                onChange={(e) => {
                  layer.text.set('fontFamily', e.target.value);
                  fabricCanvasRef.current?.renderAll();
                  setTextLayers(prev => prev.map(t => 
                    t.id === layer.id 
                      ? { ...t, text: layer.text }
                      : t
                  ));
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  marginBottom: '1rem',
                  backgroundColor: '#2D2D2D',
                  border: '1px solid #98F9B3',
                  borderRadius: '0',
                  color: '#FFFFFF',
                  fontFamily: 'Montserrat, sans-serif',
                  cursor: 'pointer'
                }}
              >
                {fontOptions.map(font => (
                  <option key={font} value={font} style={{ fontFamily: font }}>
                    {font}
                  </option>
                ))}
              </select>

              {/* Font Size Slider */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#FFFFFF',
                  fontSize: '0.875rem',
                  fontFamily: 'Montserrat, sans-serif',
                  textTransform: 'uppercase'
                }}>
                  Size: {layer.text.fontSize}px
                </label>
                <input
                  type="range"
                  min="12"
                  max="72"
                  value={layer.text.fontSize || 30}
                  onChange={(e) => {
                    const newSize = parseInt(e.target.value);
                    layer.text.set('fontSize', newSize);
                    fabricCanvasRef.current?.renderAll();
                    setTextLayers(prev => prev.map(t => 
                      t.id === layer.id 
                        ? { ...t, text: layer.text }
                        : t
                    ));
                  }}
                  style={{ 
                    width: '100%',
                    accentColor: '#98F9B3'  // For browsers that support it
                  }}
                />
              </div>

              {/* Color Picker */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#FFFFFF',
                  fontSize: '0.875rem',
                  fontFamily: 'Montserrat, sans-serif',
                  textTransform: 'uppercase'
                }}>
                  Color
                </label>
                <input
                  type="color"
                  value={layer.text.fill || '#000000'}
                  onChange={(e) => {
                    layer.text.set('fill', e.target.value);
                    fabricCanvasRef.current?.renderAll();
                    setTextLayers(prev => prev.map(t => 
                      t.id === layer.id 
                        ? { ...t, text: layer.text }
                        : t
                    ));
                  }}
                  style={{
                    width: '100%',
                    height: '40px',
                    padding: '0',
                    border: '1px solid #98F9B3',
                    borderRadius: '0',
                    backgroundColor: '#2D2D2D'
                  }}
                />
              </div>
            </div>
          ))}

          {/* Add Text Button */}
          <button
            onClick={addNewText}
            style={{
              width: '100%',
              backgroundColor: '#98F9B3',
              color: '#000000',
              padding: '0.75rem',
              borderRadius: '0',
              fontWeight: '500',
              marginBottom: '1rem',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'Montserrat, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            Add Text
          </button>
        </>
      )}

      {/* Proceed Button */}
      <button
        onClick={() => setCurrentStep(2)}
        style={{
          width: '100%',
          backgroundColor: '#98F9B3',
          color: '#000000',
          padding: '0.75rem',
          borderRadius: '0',
          fontWeight: '500',
          marginTop: 'auto',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'Montserrat, sans-serif',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}
      >
        Sizes and Pricing
      </button>
    </>
  );

  const renderSizingStep = () => {
    const totalQuantity = Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
    const pricePerShirt = getPricePerShirt(totalQuantity);
    
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Back Button */}
        <button
          onClick={() => setCurrentStep(1)}
          style={{
            width: 'auto',
            backgroundColor: 'transparent',
            color: '#98F9B3',
            padding: '0.5rem 0',
            marginBottom: '1rem',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'Montserrat, sans-serif',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          ← Back to Design
        </button>

        {/* Size Quantity Inputs */}
        <div style={{ marginBottom: '1.5rem' }}>
          {Object.entries(quantities).map(([size, quantity]) => (
            <div key={size} style={{ marginBottom: '0.75rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.25rem',
                color: '#FFFFFF',
                fontSize: '0.875rem',
                fontFamily: 'Montserrat, sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Size {size}
              </label>
              <input
                type="number"
                min="0"
                value={quantity}
                onChange={(e) => {
                  const value = Math.max(0, parseInt(e.target.value) || 0);
                  setQuantities(prev => ({
                    ...prev,
                    [size]: value
                  }));
                }}
                style={{
                  width: '100%',
                  padding: '0.4rem',
                  backgroundColor: '#3D3D3D',
                  border: '1px solid #98F9B3',
                  borderRadius: '0',
                  color: '#FFFFFF',
                  fontFamily: 'Montserrat, sans-serif',
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield',
                  margin: 0,
                  '::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0
                  },
                  '::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0
                  }
                }}
              />
            </div>
          ))}
        </div>

        {/* Total Box */}
        <div style={{
          marginBottom: '0.75rem',
          padding: '0.75rem',
          backgroundColor: '#3D3D3D',
          border: '1px solid #98F9B3',
          borderRadius: '0'
        }}>
          <div style={{
            color: '#FFFFFF',
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '0.875rem',
            marginBottom: '0.25rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Price per shirt: ${pricePerShirt}.00
          </div>
          <div style={{
            color: '#FFFFFF',
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '1.125rem',
            fontWeight: '500'
          }}>
            Total: ${calculateTotal()}.00
          </div>
        </div>

        {/* Delivery Box */}
        <div style={{
          marginBottom: '1.5rem',
          padding: '0.75rem',
          backgroundColor: '#3D3D3D',
          border: '1px solid #98F9B3',
          borderRadius: '0'
        }}>
          <div style={{
            color: '#FFFFFF',
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '0.875rem'
          }}>
            Guaranteed Delivery by 4:00 PM on {getDeliveryDate()}
          </div>
        </div>

        {/* Proceed Button */}
        <button
          onClick={() => {
            const totalQuantity = Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
            
            const canvasImage = fabricCanvasRef.current?.toDataURL({
              format: 'png',
              quality: 1
            });

            const orderData = {
              quantities: quantities,
              pricePerShirt: getPricePerShirt(totalQuantity),
              total: calculateTotal(),
              deliveryDate: `4:00 PM on ${getDeliveryDate()}`,
              designImage: canvasImage
            };
            
            navigate('/quick-order-checkout', { 
              state: { orderData }
            });
          }}
          style={{
            width: '100%',
            backgroundColor: '#98F9B3',
            color: '#000000',
            padding: '0.75rem',
            borderRadius: '0',
            fontWeight: '500',
            marginTop: 'auto',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'Montserrat, sans-serif',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
        >
          Proceed to Checkout
        </button>
      </div>
    );
  };

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'row',
      minHeight: '100vh',
      backgroundColor: 'var(--al-bg)',  // Charcoal background
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      fontFamily: 'Montserrat, sans-serif',
      textTransform: 'uppercase'
    }}>
      {/* Main canvas area */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: '2rem'
      }}>
        <div className="canvas-container" style={{ 
          border: '1px solid #98F9B3',  // Pastel green border
          backgroundColor: '#2D2D2D',    // Darker charcoal for canvas background
          borderRadius: '0',             // Remove border radius
          overflow: 'hidden',
          height: '800px',
          width: '600px'
        }}>
          <canvas ref={canvasRef} />
        </div>
      </div>

      {/* Options Panel */}
      <div style={{ 
        width: '250px',
        height: '800px',
        alignSelf: 'center'
      }}>
        <div style={{
          backgroundColor: '#2D2D2D',    // Darker charcoal
          borderRadius: '0',             // Remove border radius
          border: '1px solid #98F9B3',   // Pastel green border
          padding: '1.5rem',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          color: '#FFFFFF'               // White text
        }}>
          <h2 style={{ 
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '1.5rem',
            color: '#FFFFFF',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            {currentStep === 1 ? 'Customize Your Design' : 'Select Sizes'}
          </h2>

          {currentStep === 1 ? renderDesignStep() : renderSizingStep()}
        </div>
      </div>
    </div>
  );
}

// Update the button styles in renderDesignStep and renderSizingStep
const buttonStyles = {
  primary: {
    backgroundColor: '#98F9B3',    // Pastel green
    color: '#000000',             // Black text
    padding: '0.75rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    width: '100%',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'Montserrat, sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: '#7DE99A'   // Slightly darker pastel green on hover
    }
  },
  secondary: {
    backgroundColor: '#2D2D2D',    // Darker charcoal
    color: '#FFFFFF',             // White text
    padding: '0.75rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    width: '100%',
    border: '1px solid #98F9B3',  // Pastel green border
    cursor: 'pointer',
    fontFamily: 'Montserrat, sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: '#3D3D3D'   // Slightly lighter charcoal on hover
    }
  }
};

// Update input styles
const inputStyles = {
  backgroundColor: '#3D3D3D',     // Slightly lighter charcoal
  color: '#FFFFFF',              // White text
  border: '1px solid #98F9B3',   // Pastel green border
  borderRadius: '0.375rem',
  padding: '0.5rem',
  fontFamily: 'Montserrat, sans-serif',
  width: '100%',
  '&:focus': {
    outline: 'none',
    borderColor: '#7DE99A'       // Slightly darker pastel green on focus
  }
};

export default QuickOrder; 